import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface CalendarPickerProps {
  selectedDate: string | null;
  selectedTime: string | null;
  onDateChange: (date: string | null) => void;
  onTimeChange: (time: string | null) => void;
}

function generateTimeSlots(isWeekend: boolean): string[] {
  const slots: string[] = [];
  if (isWeekend) {
    for (let h = 8; h < 12; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
      slots.push(`${h.toString().padStart(2, '0')}:30`);
    }
  } else {
    for (let h = 17; h < 20; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
      slots.push(`${h.toString().padStart(2, '0')}:30`);
    }
  }
  return slots;
}

function isDateInPast(year: number, month: number, day: number): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(year, month, day);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
}

function isWeekend(year: number, month: number, day: number): boolean {
  const date = new Date(year, month, day);
  const dow = date.getDay();
  return dow === 0 || dow === 6;
}

function formatDateKey(year: number, month: number, day: number): string {
  return `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  const firstDay = new Date(year, month, 1).getDay();
  return firstDay === 0 ? 6 : firstDay - 1;
}

export default function CalendarPicker({
  selectedDate,
  selectedTime,
  onDateChange,
  onTimeChange,
}: CalendarPickerProps) {
  const { t } = useTranslation();

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const months = t('calendar.months', { returnObjects: true }) as string[];
  const dayNames = t('calendar.days', { returnObjects: true }) as string[];

  const goToPrevMonth = useCallback(() => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  }, [currentMonth]);

  const goToNextMonth = useCallback(() => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  }, [currentMonth]);

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOffset = getFirstDayOfMonth(currentYear, currentMonth);
    const days: { day: number; dateKey: string; disabled: boolean; isCurrentMonth: boolean }[] = [];

    const prevMonthDays = currentMonth === 0 ? getDaysInMonth(currentYear - 1, 11) : getDaysInMonth(currentYear, currentMonth - 1);
    for (let i = firstDayOffset - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      days.push({
        day,
        dateKey: formatDateKey(prevYear, prevMonth, day),
        disabled: true,
        isCurrentMonth: false,
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = formatDateKey(currentYear, currentMonth, d);
      const disabled = isDateInPast(currentYear, currentMonth, d);
      days.push({ day: d, dateKey, disabled, isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      days.push({
        day: d,
        dateKey: formatDateKey(nextYear, nextMonth, d),
        disabled: true,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  const selectedDateWeekend = useMemo(() => {
    if (!selectedDate) return false;
    const parts = selectedDate.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return isWeekend(year, month, day);
  }, [selectedDate]);

  const timeSlots = useMemo(() => {
    return generateTimeSlots(selectedDateWeekend);
  }, [selectedDateWeekend]);

  const isTimeInPast = useCallback(
    (time: string): boolean => {
      if (!selectedDate) return false;
      const now = new Date();
      const [h, m] = time.split(':').map(Number);
      const slotDate = new Date(selectedDate);
      slotDate.setHours(h, m, 0, 0);
      return slotDate <= now;
    },
    [selectedDate],
  );

  const handleDateClick = useCallback(
    (dateKey: string, disabled: boolean) => {
      if (disabled) return;
      if (selectedDate === dateKey) {
        onDateChange(null);
        onTimeChange(null);
      } else {
        onDateChange(dateKey);
        onTimeChange(null);
      }
    },
    [selectedDate, onDateChange, onTimeChange],
  );

  const handleTimeClick = useCallback(
    (time: string) => {
      if (isTimeInPast(time)) return;
      onTimeChange(selectedTime === time ? null : time);
    },
    [selectedTime, onTimeChange, isTimeInPast],
  );

  const showTimeSlots = selectedDate !== null;

  const formattedSelection = useMemo(() => {
    if (!selectedDate || !selectedTime) return null;
    const dateObj = new Date(selectedDate);
    const monthName = months[dateObj.getMonth()];
    const dayNum = dateObj.getDate();
    return `${monthName} ${dayNum}, ${selectedTime}`;
  }, [selectedDate, selectedTime, months]);

  return (
    <div className="bg-white rounded-2xl border border-background-200/70 overflow-hidden">
      <div className="p-6 md:p-8">
        <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
          {t('calendar.selectDate')}
        </h3>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-5">
          <button
            type="button"
            onClick={goToPrevMonth}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg text-foreground-600" />
          </button>
          <span className="font-heading text-base font-semibold text-foreground-900">
            {months[currentMonth]} {currentYear}
          </span>
          <button
            type="button"
            onClick={goToNextMonth}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg text-foreground-600" />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-semibold text-foreground-400 uppercase tracking-wider py-1"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-y-1">
          {calendarDays.map((d, i) => (
            <div key={i} className="flex items-center justify-center py-0.5">
              <button
                type="button"
                disabled={d.disabled}
                onClick={() => handleDateClick(d.dateKey, d.disabled)}
                className={`w-9 h-9 rounded-full text-sm font-medium flex items-center justify-center transition-all duration-150 ${
                  !d.isCurrentMonth
                    ? 'text-transparent pointer-events-none'
                    : d.disabled
                    ? 'text-foreground-300 cursor-not-allowed'
                    : selectedDate === d.dateKey
                    ? 'bg-accent-500 text-white cursor-pointer'
                    : 'text-foreground-700 hover:bg-accent-100 cursor-pointer'
                }`}
              >
                {d.day}
              </button>
            </div>
          ))}
        </div>

        {/* Divider */}
        {showTimeSlots && (
          <div className="mt-6 pt-6 border-t border-background-200/70">
            <h4 className="font-heading text-lg font-semibold text-foreground-950 mb-1">
              {t('calendar.selectTime')}
            </h4>
            <p className="text-xs text-foreground-400 mb-4">{selectedDateWeekend ? t('calendar.availableSlotsWeekend') : t('calendar.availableSlotsWeekday')}</p>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {timeSlots.map((slot) => {
                const past = isTimeInPast(slot);
                const active = selectedTime === slot;
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={past}
                    onClick={() => handleTimeClick(slot)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-150 whitespace-nowrap ${
                      past
                        ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                        : active
                        ? 'bg-accent-500 text-white cursor-pointer'
                        : 'bg-background-100 text-foreground-600 hover:bg-accent-100 cursor-pointer'
                    }`}
                  >
                    {slot}
                    <span className="block text-[10px] font-normal opacity-70">
                      {t('calendar.slotLabel')}
                    </span>
                  </button>
                );
              })}
            </div>

            {formattedSelection && (
              <div className="mt-5 flex items-center gap-2 bg-accent-50 border border-accent-200 rounded-lg px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-white">
                  <i className="ri-calendar-check-line text-sm" />
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{t('calendar.yourSelection')}</p>
                  <p className="text-sm font-semibold text-foreground-900">{formattedSelection}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {showTimeSlots && !selectedTime && (
          <div className="mt-6 pt-6 border-t border-background-200/70">
            <p className="text-sm text-foreground-400 text-center">{t('calendar.noSlots')}</p>
          </div>
        )}
      </div>
    </div>
  );
}